import { useCallback, useRef, useState } from 'react';

import {
  ADDITIONAL_INFORMATION_STATUS,
  AdditionalInformation,
} from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { type ModalElement } from '@hivebrite/design-system/Dialog';
import { Loader } from '@hivebrite/design-system/Loader';
import { type ButtonVariant } from '@hivebrite/design-system/Theme';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';

export interface ConfirmationDialogProps {
  cancelButtonText?: string;
  cancelButtonVariant?: ButtonVariant;
  children?:
    | React.ReactNode
    | ((props: { modalRef: React.RefObject<ModalElement> }) => React.ReactNode);
  confirmButtonText?: string;
  confirmButtonVariant?: ButtonVariant;
  header: string;
  hideCancelButton?: boolean;
  infoMessage?: string;
  invertButtons?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  // If onConfirm returns true, the dialog will be dismissed
  onConfirm?: () => Promise<boolean | void> | boolean | void;
  onDismiss: () => void;
}

export const ConfirmationDialog = ({
  isOpen,
  onDismiss,
  header,
  isLoading = false,
  children,
  cancelButtonText = __('Cancel'),
  cancelButtonVariant = 'secondary',
  confirmButtonText,
  confirmButtonVariant = 'primary',
  onConfirm,
  infoMessage,
  hideCancelButton = false,
  invertButtons = false,
}: ConfirmationDialogProps) => {
  const modalRef = useRef<ModalElement | null>(null);
  const [isModalMounted, setIsModalMounted] = useState(false);

  const setModalRef = useCallback((node: ModalElement | null) => {
    modalRef.current = node;
    setIsModalMounted(!!node);
  }, []);

  const handleConfirm = useCallback(async () => {
    const success = await onConfirm?.();

    if (success) {
      onDismiss();
    }
  }, [onConfirm, onDismiss]);

  const renderFooter = useCallback(() => {
    if (confirmButtonText && onConfirm) {
      return (
        <Box
          gridColumnGap={3}
          flexDirection={invertButtons ? 'row-reverse' : 'row'}
        >
          {!hideCancelButton && (
            <Button variant={cancelButtonVariant} onClick={onDismiss}>
              {cancelButtonText}
            </Button>
          )}
          <Button variant={confirmButtonVariant} onClick={handleConfirm}>
            {confirmButtonText}
          </Button>
        </Box>
      );
    }

    return null;
  }, [
    confirmButtonText,
    onConfirm,
    invertButtons,
    hideCancelButton,
    cancelButtonVariant,
    onDismiss,
    cancelButtonText,
    confirmButtonVariant,
    handleConfirm,
  ]);

  const renderChildren = useCallback(() => {
    if (!children) {
      return null;
    }

    // Only render function children when modal is mounted
    if (typeof children === 'function') {
      if (!isModalMounted) {
        return <Loader />;
      }

      return children({ modalRef });
    }

    return children;
  }, [children, modalRef, isModalMounted]);

  return (
    <ModalWithTemplate
      onCloseModal={onDismiss}
      isOpen={isOpen}
      footer={renderFooter()}
      header={header}
      ref={setModalRef}
      isLoading={isLoading}
      isSticky
      pb={0}
    >
      {/* The minHeight is to prevent the dialog from jumping when the loader spinner is shown */}
      <Box gridRowGap={6} flexDirection="column" minHeight="74px">
        {renderChildren()}
        {infoMessage && (
          <AdditionalInformation
            id="confirmation-dialog-additional-information"
            status={ADDITIONAL_INFORMATION_STATUS.INFO}
          >
            {infoMessage}
          </AdditionalInformation>
        )}
      </Box>
    </ModalWithTemplate>
  );
};
